import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import useMediaTags, { MediaTag } from '@/src/Activations/components/hooks/useMediaTags';
import { normalizeTag } from '../gallery/parts';
import { IconHeart, IconHistory, IconNext, IconPrev, IconX, isVideo, useTopmost } from './parts';

/* Full-screen viewer. Follows a fixed list of ids so a refresh or a
   hide never swaps the photo underneath you. Tags save immediately. */

export default function Lightbox({
    ids,
    byId,
    id,
    onStep,
    onClose,
    deviceName,
    onFavorite,
    onHidden,
    onReplace,
    onHistory,
    onTags,
    onError,
    busy,
}: {
    ids: number[];
    byId: Map<number, any>;
    id: number;
    onStep: (id: number) => void;
    onClose: () => void;
    deviceName: (key: string | null) => string;
    onFavorite: (m: any) => void;
    onHidden: (m: any) => void;
    onReplace: (m: any, file: File) => void;
    onHistory: (m: any) => void;
    onTags: (mediaId: number, tags: string[]) => void;
    onError: (text: string) => void;
    busy: Set<number>;
}) {
    const { getTagsForMedia, addTags, removeTags } = useMediaTags();
    const [tags, setTags] = useState<MediaTag[] | null>(null);
    const [tagsFailed, setTagsFailed] = useState(false);
    const [draft, setDraft] = useState('');
    const [tagBusy, setTagBusy] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const isTop = useTopmost(true);

    const list = ids.filter((x) => byId.has(x));
    const idx = list.indexOf(id);
    const m = byId.get(id);
    const prev = idx > 0 ? list[idx - 1] : null;
    const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;

    useEffect(() => {
        let mounted = true;
        setTags(null);
        setTagsFailed(false);
        setDraft('');
        getTagsForMedia(id)
            .then((r) => mounted && setTags(Array.isArray(r.data) ? r.data : []))
            .catch(() => mounted && setTagsFailed(true));
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const keys = useRef({ prev, next, onStep, onClose });
    keys.current = { prev, next, onStep, onClose };
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!isTop()) return;
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) {
                if (e.key === 'Escape') (t as HTMLInputElement).blur();
                return;
            }
            const k = keys.current;
            if (e.key === 'Escape') k.onClose();
            if (e.key === 'ArrowLeft' && k.prev != null) k.onStep(k.prev);
            if (e.key === 'ArrowRight' && k.next != null) k.onStep(k.next);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!m) return null;

    const addTag = async () => {
        const t = normalizeTag(draft);
        if (!t || tagBusy) return;
        if ((tags ?? []).some((x) => x.tag === t)) {
            setDraft('');
            return;
        }
        setTagBusy(true);
        const res = await addTags(m.id, [t]).catch(() => null);
        setTagBusy(false);
        if (res == null || !Array.isArray(res.data)) {
            onError(`Couldn’t add “${t}” — try again.`);
            return;
        }
        setDraft('');
        setTags(res.data);
        onTags(m.id, res.data.map((x) => x.tag));
    };

    const removeTag = async (t: string) => {
        if (tagBusy) return;
        const before = tags ?? [];
        const after = before.filter((x) => x.tag !== t);
        setTagBusy(true);
        setTags(after);
        onTags(m.id, after.map((x) => x.tag));
        const res = await removeTags(m.id, [t]).catch(() => null);
        setTagBusy(false);
        if (res == null) {
            setTags(before);
            onTags(m.id, before.map((x) => x.tag));
            onError(`Couldn’t remove “${t}” — it’s still on the photo.`);
        }
    };

    const when = m.capturedAt ?? m.createdAt;
    const rowBusy = busy.has(m.id);

    return (
        <div className="med-lb" role="dialog" aria-modal="true" aria-label="Photo viewer" onMouseDown={onClose}>
            <div className="med-lb-top" onMouseDown={(e) => e.stopPropagation()}>
                <span className="ev-mono">{idx >= 0 ? `${idx + 1} / ${list.length}` : ''}</span>
                <button type="button" className="med-lb-ic" aria-label="Close viewer" onClick={onClose}>
                    <IconX />
                </button>
            </div>
            <div className="med-lb-stage">
                {prev != null && (
                    <button
                        type="button"
                        className="med-lb-nav l"
                        aria-label="Previous"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => onStep(prev)}
                    >
                        <IconPrev />
                    </button>
                )}
                {isVideo(m) ? (
                    <video key={m.id} src={m.originalUri} controls className="med-lb-media" onMouseDown={(e) => e.stopPropagation()} />
                ) : (
                    <img
                        key={m.id}
                        src={m.thumbnail_800pxUri ?? m.originalUri ?? m.thumbnailUri}
                        alt=""
                        className={m.isHidden ? 'med-lb-media hid' : 'med-lb-media'}
                        onMouseDown={(e) => e.stopPropagation()}
                    />
                )}
                {next != null && (
                    <button
                        type="button"
                        className="med-lb-nav r"
                        aria-label="Next"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => onStep(next)}
                    >
                        <IconNext />
                    </button>
                )}
            </div>

            <div className="med-lb-tags" onMouseDown={(e) => e.stopPropagation()}>
                {tagsFailed && <span className="med-lb-dim">Couldn’t load tags.</span>}
                {(tags ?? []).map((t) => {
                    const booth = t.source === 'capture' || t.source === 'action';
                    return (
                        <span key={t.tag} className={booth ? 'med-lb-tag booth' : 'med-lb-tag'} title={booth ? `Applied by the booth (${t.source})` : 'Added by an operator'}>
                            {t.tag}
                            <button type="button" aria-label={`Remove tag ${t.tag}`} disabled={tagBusy} onClick={() => removeTag(t.tag)}>
                                ×
                            </button>
                        </span>
                    );
                })}
                <input
                    className="med-lb-in"
                    aria-label="Add a tag"
                    value={draft}
                    disabled={tags == null || tagBusy}
                    placeholder={tags == null && !tagsFailed ? 'Loading tags…' : '+ add tag'}
                    spellCheck={false}
                    autoComplete="off"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            addTag();
                        }
                    }}
                />
            </div>

            <div className="med-lb-bar" onMouseDown={(e) => e.stopPropagation()}>
                <span className="med-lb-meta ev-mono">
                    {deviceName(m.deviceKeyId ?? null)}
                    {when && ` · ${moment(when).format('MMM D, h:mm A')}`}
                    {m.isHidden && ' · HIDDEN'}
                </span>
                <span className="med-lb-acts">
                    <button type="button" aria-pressed={!!m.isFavorite} disabled={rowBusy} onClick={() => onFavorite(m)}>
                        <IconHeart filled={!!m.isFavorite} />
                        {m.isFavorite ? 'Unfavorite' : 'Favorite'}
                    </button>
                    <button type="button" disabled={rowBusy} onClick={() => onHidden(m)}>
                        {m.isHidden ? 'Show' : 'Hide'}
                    </button>
                    <button type="button" disabled={rowBusy} onClick={() => fileRef.current?.click()}>
                        {rowBusy ? 'Working…' : 'Replace'}
                    </button>
                    <button type="button" onClick={() => onHistory(m)}>
                        <IconHistory />
                        History
                    </button>
                    <a href={m.originalUri} target="_blank" rel="noreferrer">
                        Open original
                    </a>
                </span>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) onReplace(m, f);
                    }}
                />
            </div>
        </div>
    );
}
